import { usePlaybackSession } from 'connections/playbackSession'
import { getAndVerifyAccountKeyPair } from 'connections/auth/handshake'
import { Ref, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  IconDefinition,
  faArrowRotateRight,
  faCode,
  faExternalLink,
  faGauge,
  faMicrochip,
  faPlugCircleXmark,
  faSignOut,
  faUser
} from '@fortawesome/free-solid-svg-icons'
import { abbreviatedSha } from '~build/git'
import { Colors } from 'design/colors'
import { Dashboard } from './tabs/dashboard/Dashboard'
import { useAuth } from 'hooks/useAuth'
import { usePlayback } from 'hooks/usePlayback'
import { useReset } from 'hooks/useReset'
import { signOut } from 'firebase/auth'
import { firebaseAuth } from 'setup/firebase'
import { CollectionId, getRecord } from 'utils/firestore'
import { Devices } from './tabs/devices/Devices'
import { MIN_FW_VERSION, useDevices } from 'hooks/useDevices'
import { Account } from './tabs/account/Account'
import { versionLessThan } from 'utils/common'
import { Header } from 'components/header/Header'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Button } from 'components/Button'

const DOCS_URL = 'https://docs.salientmotion.com/'

export enum HomeTabs {
  Dashboard,
  Devices,
  Account,
  // The following are not real tab IDs!
  Disconnect,
  SignOut,
  Docs,
  Version
}

type TabDef = {
  id: HomeTabs
  icon: IconDefinition
  iconColor?: string
  iconRef?: Ref<SVGSVGElement>
  title: string
  titleColor?: string
  onClick: () => void
  hidden?: boolean
}

type TabsDef = {
  top: TabDef[]
  bottom: TabDef[]
}

const Tab = (props: TabDef & { active: boolean; showTitle: boolean }) => {
  const [hovering, setHovering] = useState<boolean>(false)

  let backgroundColor: string | undefined
  if (props.active) {
    backgroundColor = `${Colors.BLACK}33`
  } else if (hovering) {
    backgroundColor = `${Colors.WHITE}11`
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        backgroundColor
      }}
      onClick={props.onClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <FontAwesomeIcon
        ref={props.iconRef}
        icon={props.icon}
        color={props.iconColor || Colors.WHITE}
        size="2x"
        style={{ padding: 16 }}
      />
      {props.showTitle ? (
        <div
          style={{
            color: props.titleColor || Colors.WHITE,
            fontSize: 16,
            fontWeight: 'bold',
            padding: 16
          }}
        >
          {props.title}
        </div>
      ) : null}
    </div>
  )
}

const Tabs = (props: {
  tabs: TabsDef
  currentTab: HomeTabs
  setCurrentTab: (tab: HomeTabs) => void
  setWidth: (width: number) => void
}) => {
  const [hovering, setHovering] = useState<boolean>(false)
  const { setWidth, tabs, currentTab } = props

  const topTabs = useMemo(() => {
    return tabs.top
      .filter((tab) => !tab.hidden)
      .map((tab, index) => {
        return (
          <Tab
            key={`home-tab-${tab.id}`}
            iconRef={(e) =>
              e === null ? undefined : index === 0 ? setWidth(e.clientWidth) : undefined
            }
            {...tab}
            active={currentTab === tab.id}
            showTitle={hovering}
          />
        )
      })
  }, [tabs, currentTab, hovering, setWidth])

  const bottomTabs = useMemo(() => {
    return tabs.bottom
      .filter((tab) => !tab.hidden)
      .map((tab) => {
        return (
          <Tab
            key={`home-tab-${tab.id}`}
            {...tab}
            active={currentTab === tab.id}
            showTitle={hovering}
          />
        )
      })
  }, [tabs, currentTab, hovering])

  return (
    <div
      style={{
        backgroundColor: Colors.DARK_GREEN,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: 'fit-content',
        height: '100%',
        position: 'absolute',
        boxShadow: '1px 2px 4px #222222',
        zIndex: 1
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div>{topTabs}</div>
      <div>{bottomTabs}</div>
    </div>
  )
}

export const Home = () => {
  const [currentTab, setCurrentTab] = useState<HomeTabs>(HomeTabs.Dashboard)
  const [tabsWidth, setTabsWidth] = useState<number>(0)
  const { user } = useAuth()
  const {
    devices,
    connectedDevice,
    connect,
    refresh: refreshDeviceList,
    updateKeyPair
  } = useDevices()
  const { playbackSession } = usePlaybackSession()
  const wereDevicesConnectedRef = useRef<boolean>(
    (devices?.filter((d) => d.initialized && d.attached).length ?? 0) > 0
  )
  const isFirstRender = useRef<boolean>(true)
  const reset = useReset()
  const { live } = usePlayback()

  useEffect(() => {
    refreshDeviceList()
  }, [refreshDeviceList])

  // Device connection handlers
  useEffect(() => {
    if (devices === undefined || devices.some((d) => !d.initialized)) {
      // Device list not yet populated
      return
    }
    const connectedDevices = devices.filter((d) => d.initialized && d.attached)

    if (!playbackSession?.isPlayback) {
      if (connectedDevices.length === 1) {
        const noConnection = connectedDevice === undefined
        const isNew = !wereDevicesConnectedRef.current

        if (isFirstRender.current || (noConnection && isNew)) {
          // Auto-connect if one device is plugged in
          const device = connectedDevices[0]
          if (device.initialized && !versionLessThan(device.fwVersion, MIN_FW_VERSION)) {
            connect(device)
            live()
          }
        }
      } else if (connectedDevices.length > 1 && isFirstRender.current) {
        // Navigate to devices tab if >1 device is plugged in
        setCurrentTab(HomeTabs.Devices)
      }
    }

    wereDevicesConnectedRef.current = connectedDevices.length > 0
    isFirstRender.current = false
  }, [playbackSession, connect, connectedDevice, currentTab, devices, live])

  // Only fetch and set keypair if user is present; otherwise, skip (allow telemetry without sign-in)
  useEffect(() => {
    if (!user) {
      return
    }
    getRecord(CollectionId.Users, user.uid).then((userDetails) => {
      let keypair
      if (userDetails?.keypair !== undefined) {
        keypair = getAndVerifyAccountKeyPair(userDetails.keypair)
        updateKeyPair(keypair)
      }
    })
  }, [user, updateKeyPair])

  const tabs: TabsDef = useMemo(() => {
    return {
      top: [
        {
          id: HomeTabs.Dashboard,
          icon: faGauge,
          title: 'Dashboard',
          onClick: () => setCurrentTab(HomeTabs.Dashboard)
        },
        {
          id: HomeTabs.Devices,
          icon: faMicrochip,
          title: 'Devices',
          onClick: () => setCurrentTab(HomeTabs.Devices)
        },
        {
          id: HomeTabs.Account,
          icon: faUser,
          title: 'Account',
          onClick: () => setCurrentTab(HomeTabs.Account)
        }
      ],
      bottom: [
        {
          id: HomeTabs.Disconnect,
          icon: faPlugCircleXmark,
          title: playbackSession?.isPlayback ? 'Stop Playback' : 'Disconnect',
          onClick: reset,
          hidden: connectedDevice === undefined && !playbackSession?.isPlayback
        },
        {
          id: HomeTabs.Docs,
          icon: faExternalLink,
          title: 'Docs',
          onClick: () => window.open(DOCS_URL, '_blank')?.focus()
        },
        {
          id: HomeTabs.SignOut,
          icon: faSignOut,
          title: 'Sign Out',
          onClick: () => {
            signOut(firebaseAuth)
          }
        },
        {
          id: HomeTabs.Version,
          icon: faCode,
          title: `v${import.meta.env.VITE_APP_VERSION}-${abbreviatedSha?.slice(0, 7)}`,
          onClick: () => {}
        }
      ]
    }
  }, [connectedDevice, playbackSession, reset])

  let content
  switch (currentTab) {
    case HomeTabs.Dashboard:
      content = <Dashboard loadDevicesScreen={() => setCurrentTab(HomeTabs.Devices)} />
      break
    case HomeTabs.Devices:
      content = <Devices />
      break
    case HomeTabs.Account:
      content = <Account />
      break
  }

  return (
    <>
      <Header />
      <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
        <div style={{ flex: 1, display: 'flex', overflow: 'auto' }}>
          <div style={{ width: tabsWidth }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{content}</div>
          <Tabs
            tabs={tabs}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            setWidth={setTabsWidth}
          />
        </div>
      </ErrorBoundary>
    </>
  )
}

const ErrorFallbackComponent = ({ error, resetErrorBoundary }: FallbackProps) => {
  const errorMessage =
    error instanceof Error
      ? 'Error: ' + error.message + '\nStack Trace: ' + error.stack
      : 'Unknown error: ' + String(error)

  return (
    <div role="alert" style={{ display: 'block', padding: 32, color: Colors.WHITE }}>
      <p>
        Something went wrong: please reload the application and try again. If you have enabled
        analytics consent, this error has been automatically reported to our team. We apologize for
        the inconvenience. If your issue persists, please contact support with a description of your
        actions and the following error context:
      </p>
      <pre>{errorMessage}</pre>
      <Button
        style={{ width: 200, height: 40 }}
        onClick={resetErrorBoundary}
        backgroundColor={Colors.BUTTON_GREEN}
        textColor={Colors.WHITE}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
        >
          <FontAwesomeIcon
            icon={faArrowRotateRight}
            color={Colors.WHITE}
            style={{ padding: '0px 4px' }}
            size="lg"
          />
          <span>Clear Errors</span>
        </div>
      </Button>
    </div>
  )
}
